import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2 } from 'lucide-react';
import type { ImportReport } from '../../api/imagePresets';
import { presetError } from '../../api/imagePresets';
import { useImportPresets } from '../../hooks';
import { getErrorDetail } from '../../utils/error';
import { Modal } from './common';
import { serverEnv } from './format';

const Section: React.FC<{
  title: string;
  groups: [label: string, ids: string[], tone: string][];
  picked?: string[];
  pickable?: string[];
  onPick?: (id: string) => void;
}> = ({ title, groups, picked, pickable, onPick }) => (
  <div>
    <p className="text-sm font-semibold text-slate-700 mb-1.5">{title}</p>
    <div className="space-y-1.5">
      {groups
        .filter(([, ids]) => ids.length)
        .map(([label, ids, tone]) => (
          <div key={label} className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className={`badge ${tone}`}>{label}</span>
            {ids.map((id) =>
              pickable?.includes(id) ? (
                <label
                  key={id}
                  className="inline-flex items-center gap-1 font-mono rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5"
                >
                  <input type="checkbox" checked={picked?.includes(id)} onChange={() => onPick?.(id)} />
                  {id}
                </label>
              ) : (
                <span key={id} className="font-mono text-slate-600">
                  {id}
                </span>
              ),
            )}
          </div>
        ))}
    </div>
  </div>
);

/**
 * 다른 환경에서 내보낸 묶음을 들인다. 먼저 판정(dry run)을 보여 주고, 내용이 다른 것은
 * 하나씩 덮어쓰기를 골라야 들어간다. 들인 프리셋은 꺼진 채 — 켜는 건 사람이 한다.
 */
const ImportDialog: React.FC<{ onClose: () => void; onImported: () => void }> = ({ onClose, onImported }) => {
  const { t } = useTranslation();
  const run = useImportPresets();
  const [bundle, setBundle] = useState<Record<string, unknown> | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [plan, setPlan] = useState<ImportReport | null>(null);
  const [done, setDone] = useState<ImportReport | null>(null);
  const [overwrite, setOverwrite] = useState<string[]>([]);
  const [overwriteImages, setOverwriteImages] = useState<string[]>([]);
  const toggle = (set: React.Dispatch<React.SetStateAction<string[]>>) => (id: string) =>
    set((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const load = async (file: File | undefined) => {
    setPlan(null);
    setDone(null);
    setFileError(null);
    run.reset();
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      setBundle(parsed);
      setPlan(await run.mutateAsync({ bundle: parsed, dryRun: true }));
    } catch (e) {
      setBundle(null);
      setFileError(e instanceof SyntaxError ? t('imagePresets.importNotJson') : null);
    }
  };

  const apply = async () => {
    if (!bundle) return;
    try {
      setDone(await run.mutateAsync({ bundle, dryRun: false, overwrite, overwriteImages }));
      onImported();
    } catch {
      // 아래에 보인다.
    }
  };

  const error = presetError(run.error);
  const nothingToDo =
    plan &&
    plan.presets.new.length + overwrite.length === 0 &&
    plan.reference_images.new.length + overwriteImages.length === 0;

  return (
    <Modal title={t('imagePresets.importTitle', { env: serverEnv() })} onClose={onClose}>
      {done ? (
        <div className="space-y-3 text-sm">
          <p className="flex items-center gap-2 text-emerald-700 font-medium">
            <CheckCircle2 size={18} /> {t('imagePresets.importDone')}
          </p>
          <p className="text-slate-600">{t('imagePresets.importDoneHint')}</p>
          <button type="button" className="btn-primary text-sm" onClick={onClose}>
            {t('imagePresets.close')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{t('imagePresets.importIntro')}</p>
          <input
            type="file"
            accept="application/json,.json"
            onChange={(e) => load(e.target.files?.[0])}
            aria-label={t('imagePresets.importFile')}
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200"
          />
          {run.isPending && <Loader2 className="animate-spin text-slate-400" />}
          {(fileError || run.error) && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 space-y-1" role="alert">
              <p>{fileError ?? getErrorDetail(run.error, t('imagePresets.importFailed'))}</p>
              {error?.errors?.map((e) => (
                <p key={e.loc.join('.') + e.msg} className="text-xs font-mono">
                  {e.loc.join('.')}: {e.msg}
                </p>
              ))}
            </div>
          )}
          {plan && (
            <div className="space-y-4 rounded-lg border border-slate-200 p-4">
              <Section
                title={t('imagePresets.tabPresets')}
                groups={[
                  [t('imagePresets.importNew'), plan.presets.new, 'badge-success'],
                  [t('imagePresets.importConflict'), plan.presets.conflict, 'badge-warning'],
                  [t('imagePresets.importSame'), plan.presets.same, 'bg-slate-100 text-slate-600'],
                ]}
                picked={overwrite}
                pickable={plan.presets.conflict}
                onPick={toggle(setOverwrite)}
              />
              <Section
                title={t('imagePresets.tabReferences')}
                groups={[
                  [t('imagePresets.importNew'), plan.reference_images.new, 'badge-success'],
                  [t('imagePresets.importConflict'), plan.reference_images.conflict, 'badge-warning'],
                  [t('imagePresets.importSame'), plan.reference_images.same, 'bg-slate-100 text-slate-600'],
                ]}
                picked={overwriteImages}
                pickable={plan.reference_images.conflict}
                onPick={toggle(setOverwriteImages)}
              />
              <Section
                title={t('imagePresets.tabCategories')}
                groups={[
                  [t('imagePresets.importNew'), plan.categories.new, 'badge-success'],
                  [t('imagePresets.importExisting'), plan.categories.existing, 'bg-slate-100 text-slate-600'],
                ]}
              />
              {(plan.presets.conflict.length > 0 || plan.reference_images.conflict.length > 0) && (
                <p className="text-xs text-amber-700">{t('imagePresets.importConflictHint')}</p>
              )}
              <p className="text-xs text-slate-500">{t('imagePresets.importDisabledNote')}</p>
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={apply}
                disabled={!!nothingToDo || run.isPending}
              >
                {t('imagePresets.importApply')}
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ImportDialog;
